import { useEffect, useRef } from 'react';
import { useFormApi, useFormState } from 'informed';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getBlockNameByType } from 'lib/blocks';
import { Button } from 'components/common';
import { Icon } from 'components/common/icons';
import {
  Checkbox,
  DisplayField,
  FormGroup,
  NumberStepper,
  RadioButtonCard,
  RadioGroup,
  TextArea
} from 'components/common/form';
import { EditorBlockMoreSettings } from 'components/editor/blocks';

import type { AISupportedBlocks } from 'shared-types';
import type { FormValues, View } from './AIGenerateContentButton';

interface Props {
  availableTypes: string[];
  blockId: number;
  blockIdHasContext: boolean;
  isSelectionAvailable: boolean;
  getValidate: React.MutableRefObject<() => boolean>;
  onClearContextSelection: () => void;
  onViewChange: (view: View) => void;
  numberOfContextBlocks: number;
  type: AISupportedBlocks;
  visible: boolean;
}

const AIGenerateContentSettings = ({
  availableTypes,
  blockId,
  blockIdHasContext,
  isSelectionAvailable,
  getValidate,
  onClearContextSelection,
  onViewChange,
  numberOfContextBlocks,
  type,
  visible
}: Props) => {
  const formApi = useFormApi<FormValues>();
  const { errors, values } = useFormState<FormValues>();

  const { messages, theme } = useReduxState();

  const contextValidationTimeout = useRef<NodeJS.Timeout>(undefined);
  const promptValidationTimeout = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => {
    if (!visible) {
      formApi.setError('context', undefined);
      formApi.setError('prompt', undefined);
    }
  }, [visible]);

  useEffect(() => {
    getValidate.current = handleSubmit;
  }, [getValidate]);

  const handleClear = () => {
    formApi.setValue('blockIds', []);
    onClearContextSelection();
  };

  const handleSubmit = () => {
    return validateContext(values.context) && validatePrompt(values.prompt);
  };

  const validateContext = (context: string) => {
    const validContext = context?.trim();

    const error = formApi.getError('context');
    const useContentFromSelf = !blockId
      ? false
      : formApi.getValue('useContentFromSelf');
    const useExternalKnowledge = formApi.getValue('useExternalKnowledge');

    const hasContextBlocks = Boolean(
      (useContentFromSelf && blockId) ||
        Object.keys(values.blockIds || {}).length
    );

    const shouldError =
      !validContext && !hasContextBlocks && !useExternalKnowledge;
    if (shouldError && !error) {
      formApi.setError('context', messages.commonFieldRequired);
      return false;
    } else if (!shouldError && error) formApi.setError('context', undefined);

    return true;
  };

  const validateContextOnBooleanChange = () => {
    if (!visible) return;
    const context = formApi.getValue('context') as string | undefined;
    validateContext(context || '');
  };

  const validateContextOnTextAreaChange = (value: string | undefined) => {
    if (!visible) return;
    clearTimeout(contextValidationTimeout.current);
    contextValidationTimeout.current = setTimeout(
      () => validateContext(value || ''),
      300
    );
  };

  const validatePrompt = (value: string) => {
    const cleanPrompt = value?.trim();

    const error = formApi.getError('prompt');
    if (!cleanPrompt && !error) {
      formApi.setError('prompt', messages.commonFieldRequired);
      return false;
    } else if (cleanPrompt && error) formApi.setError('prompt', undefined);

    return true;
  };

  const validatePromptOnChange = (value: string | undefined) => {
    if (!visible) return;
    clearTimeout(promptValidationTimeout.current);
    promptValidationTimeout.current = setTimeout(
      () => validatePrompt(value || ''),
      300
    );
  };

  const blockNames = getBlockNameByType({ messages });

  return (
    <div className="settings">
      {type ? (
        <DisplayField field="type" />
      ) : (
        <FormGroup label={messages.aiGenerateContentChooseType} maxWidth="100%">
          <RadioGroup id="ai-generate-content-type" field="type">
            <div className="types">
              {availableTypes.map((type) => (
                <div className="type" key={type}>
                  <RadioButtonCard
                    id={`ai-generate-content-type-${type}`}
                    field="type"
                    value={type}
                  >
                    <div className="type-inner">
                      <div className="type-title">{blockNames[type]}</div>
                    </div>
                  </RadioButtonCard>
                </div>
              ))}
            </div>
          </RadioGroup>
        </FormGroup>
      )}

      <FormGroup
        label={messages.aiGenerateContentPrompt}
        maxWidth="100%"
        style={{ marginTop: type ? 0 : 25 }}
      >
        <TextArea
          autoGrow={true}
          error={errors?.prompt}
          field="prompt"
          onValueChange={validatePromptOnChange}
          placeholder={messages.aiGenerateContentPromptPlaceholder}
        />
      </FormGroup>

      <EditorBlockMoreSettings
        settingsHaveBeenEdited={Boolean(
          values?.useContentFromSelf ||
            values?.useCourseAndChapterTitle ||
            values?.useExternalKnowledge ||
            values?.context ||
            (numberOfContextBlocks && numberOfContextBlocks > 0)
        )}
        settingsHaveBeenEditedTooltip={
          messages.aiGenerateContentContextOptionsSelected
        }
        style={{ backgroundColor: theme.lightGrey }}
        title={messages.aiGenerateContentContextOptions}
      >
        <FormGroup>
          {blockId && blockIdHasContext ? (
            <Checkbox
              field="useContentFromSelf"
              label={messages.aiGenerateContentIncludeCurrentBlock}
              onValueChange={validateContextOnBooleanChange}
              style={{ marginBottom: 5 }}
            />
          ) : (
            <DisplayField field="useContentFromSelf" />
          )}

          <Checkbox
            field="useCourseAndChapterTitle"
            label={messages.aiGenerateContentIncludeCourseAndChapter}
            onValueChange={validateContextOnBooleanChange}
          />

          <Checkbox
            field="useExternalKnowledge"
            label={messages.aiGenerateContentAllowOwnKnowledge}
            labelTooltip={messages.aiGenerateContentAllowOwnKnowledgeInfo}
            onValueChange={validateContextOnBooleanChange}
            style={{ marginTop: 5 }}
          />
        </FormGroup>

        <FormGroup
          label={messages.aiGenerateContentChapterContext}
          maxWidth="100%"
          style={{ marginTop: 20 }}
        >
          {!isSelectionAvailable ? (
            <div className="context-blocks-empty">
              {messages.aiGenerateContentChapterContextNone}
            </div>
          ) : numberOfContextBlocks > 0 ? (
            <div className="context-blocks">
              <div className="context-blocks-status">
                <div className="context-blocks-icon">
                  <Icon icon="check" />
                </div>

                <div>{`${numberOfContextBlocks} ${messages.editorHeaderBlocksSelected}`}</div>
              </div>

              <div>
                <Button
                  category="light"
                  icon="times"
                  onClick={handleClear}
                  size="small"
                  text={messages.commonClear}
                />

                <Button
                  category="light"
                  icon="edit"
                  onClick={() => onViewChange('selection')}
                  size="small"
                  text={messages.commonEdit}
                  style={{ marginLeft: 5 }}
                />
              </div>
            </div>
          ) : (
            <Button
              category="light"
              icon="plus"
              onClick={() => onViewChange('selection')}
              size="small"
              style={{ marginBottom: 5 }}
              text={messages.aiGenerateContentSelectContent}
            />
          )}
        </FormGroup>

        <FormGroup
          label={messages.aiGenerateContentManualContext}
          labelTooltip={messages.aiGenerateContentManualContextInfo}
          labelIsOptional={values.useExternalKnowledge}
          maxWidth="100%"
          style={{ marginTop: 20 }}
        >
          <TextArea
            autoGrow={true}
            error={errors?.context}
            field="context"
            onValueChange={validateContextOnTextAreaChange}
            placeholder={messages.aiGenerateContentManualContextPlaceholder}
          />
        </FormGroup>
      </EditorBlockMoreSettings>

      {blockId ? (
        <DisplayField field="numberOfBlocks" />
      ) : (
        <FormGroup
          label={messages.aiGenerateContentNumberOfBlocks}
          labelTooltip={messages.aiGenerateContentNumberOfBlocksHelpText}
        >
          <NumberStepper
            field="numberOfBlocks"
            label={getMessageWithPlaceholders({
              message: messages.commonNumberOfAlternatives,
              placeholders: { numberOfAlternatives: '' }
            })}
            max={3}
            min={1}
            stepValue={1}
          />
        </FormGroup>
      )}

      <style jsx>{`
        .settings {
          display: ${visible ? 'block' : 'none'};
          padding: 15px;
        }
        .types {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          margin-top: 5px;
        }
        .type {
          width: 100%;
        }
        .context-summary {
          font-style: italic;
          font-weight: 400;
          margin-top: -10px;
        }
        .context-description {
          font-weight: 400;
        }
        .context-blocks {
          align-items: center;
          display: flex;
          gap: 20px;
          margin-bottom: 5px;
        }
        .context-blocks-status {
          align-items: center;
          background-color: white;
          border-radius: ${theme.inputBorderRadius};
          display: flex;
          font-size: 1.4rem;
          gap: 7px;
          padding: 5px 10px;
        }
        .context-blocks-icon {
          align-items: center;
          background-color: ${theme.successColor};
          border-radius: 40px;
          color: ${theme.successTextColor};
          display: inline-flex;
          font-size: 0.8rem;
          height: 16px;
          justify-content: center;
          width: 16px;
        }
        .context-blocks-empty {
          color: ${theme.textColorLighter};
          font-weight: 400;
        }
        @media (min-width: ${breakpoints.small}px) {
          .type {
            width: calc(50% - 7.5px);
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .type {
            width: calc(33.333% - 10px);
          }
        }
      `}</style>
    </div>
  );
};

export default AIGenerateContentSettings;
